import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import { ChatMessage } from './types';

const POLL_INTERVAL_MS = 3000;

// Polls for new messages every few seconds while mounted — there is no
// real-time transport (socket.io) in this backend, so short-interval polling
// is the delivery mechanism for this first version of 1:1 chat.
export const useCompatibilityChat = (requestId: string | undefined) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sending, setSending] = useState<boolean>(false);
  const lastMessageTimeRef = useRef<string | undefined>(undefined);

  const poll = useCallback(async () => {
    if (!requestId) return;
    try {
      const res = await api.get(`/backend/compatibility/request/${requestId}/messages`, {
        params: lastMessageTimeRef.current ? { since: lastMessageTimeRef.current } : undefined,
      });
      const incoming: ChatMessage[] = res.data.data;
      if (incoming.length > 0) {
        setMessages((prev) => [...prev, ...incoming]);
        lastMessageTimeRef.current = incoming[incoming.length - 1].createdAt;
      }
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (!requestId) return;
    setMessages([]);
    lastMessageTimeRef.current = undefined;
    setLoading(true);

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [requestId, poll]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!requestId) return;
      setSending(true);
      try {
        const res = await api.post(`/backend/compatibility/request/${requestId}/messages`, { text });
        const message: ChatMessage = res.data.data;
        setMessages((prev) => [...prev, message]);
        lastMessageTimeRef.current = message.createdAt;
      } finally {
        setSending(false);
      }
    },
    [requestId]
  );

  return { messages, loading, sending, sendMessage };
};
